import React, {useCallback, useMemo, useState} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import MuiTooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';
import {Chart} from 'react-chartjs-2';
import Typography from '@mui/material/Typography';
import {MinimalCutSet} from '../../../../utils/minimal-cut-sets';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

interface ParetoChartProps {
    cutSets: MinimalCutSet[];
    totalProbability: number;
    onBarClick?: (cutSetIndex: number) => void;
    chartRef?: React.RefObject<ChartJS | null>;
}

const DEFAULT_LIMIT = 50;
const LIMIT_OPTIONS = [50, 100, 200, 500];

/** Inline Chart.js plugin that draws dashed horizontal reference lines on the y1 (cumulative) axis. */
export const thresholdLinesPlugin = {
    id: 'paretoThresholdLines',
    afterDraw(chart: ChartJS): void {
        const y1Scale = chart.scales['y1'];
        if (!y1Scale) return;
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;

        for (const threshold of [80, 95]) {
            const yPixel = y1Scale.getPixelForValue(threshold);
            if (yPixel < chartArea.top || yPixel > chartArea.bottom) continue;

            ctx.save();
            ctx.setLineDash([4, 4]);
            ctx.lineWidth = 1;
            ctx.strokeStyle = threshold === 80 ? 'rgba(255, 152, 0, 0.6)' : 'rgba(211, 47, 47, 0.5)';
            ctx.beginPath();
            ctx.moveTo(chartArea.left, yPixel);
            ctx.lineTo(chartArea.right, yPixel);
            ctx.stroke();

            ctx.setLineDash([]);
            ctx.fillStyle = ctx.strokeStyle;
            ctx.font = '10px sans-serif';
            ctx.textAlign = 'right';
            ctx.fillText(`${threshold}%`, chartArea.right - 2, yPixel - 3);
            ctx.restore();
        }
    },
};

const ParetoChart: React.FC<ParetoChartProps> = ({ cutSets, totalProbability, onBarClick, chartRef }) => {
    const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);

    const paretoData = useMemo(() => {
        if (totalProbability === 0) return { allSorted: [], cutSetsAt80: 0, cutSetsAt95: 0 };

        const allSorted = cutSets
            .map((cs, i) => ({ index: i + 1, probability: cs.probability }))
            .sort((a, b) => b.probability - a.probability);

        let cumSum = 0;
        let cutSetsAt80 = 0;
        let cutSetsAt95 = 0;
        for (let i = 0; i < allSorted.length; i++) {
            cumSum += allSorted[i].probability;
            const pct = (cumSum / totalProbability) * 100;
            if (cutSetsAt80 === 0 && pct >= 80) cutSetsAt80 = i + 1;
            if (cutSetsAt95 === 0 && pct >= 95) cutSetsAt95 = i + 1;
        }

        return { allSorted, cutSetsAt80, cutSetsAt95 };
    }, [cutSets, totalProbability]);

    const displayLimit = Math.min(paretoData.allSorted.length, limit);
    const showSelector = paretoData.allSorted.length > DEFAULT_LIMIT;

    const chartData = useMemo(() => {
        const items = paretoData.allSorted.slice(0, displayLimit);
        let cumSum = 0;

        return {
            labels: items.map((d) => `#${d.index}`),
            datasets: [
                {
                    type: 'bar' as const,
                    label: 'Contribution %',
                    data: items.map((d) => (d.probability / totalProbability) * 100),
                    backgroundColor: 'rgba(25, 118, 210, 0.7)',
                    borderColor: 'rgba(25, 118, 210, 1)',
                    borderWidth: 1,
                    yAxisID: 'y',
                    order: 2,
                },
                {
                    type: 'line' as const,
                    label: 'Cumulative %',
                    data: items.map((d) => {
                        cumSum += d.probability;
                        return (cumSum / totalProbability) * 100;
                    }),
                    borderColor: 'rgba(244, 67, 54, 1)',
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    borderWidth: 2,
                    pointRadius: 0,
                    pointHoverRadius: displayLimit <= 30 ? 4 : 2,
                    fill: true,
                    yAxisID: 'y1',
                    order: 1,
                },
            ],
        };
    }, [paretoData, displayLimit, totalProbability]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
        plugins: {
            title: { display: false },
            legend: {
                position: 'bottom' as const,
                labels: { font: { size: 11 } },
            },
            tooltip: {
                callbacks: {
                    label: (ctx: { dataset: { label?: string }; raw: unknown }): string => {
                        const val = ctx.raw as number;
                        return `${ctx.dataset.label}: ${val.toFixed(1)}%`;
                    },
                },
            },
        },
        scales: {
            x: {
                title: { display: true, text: 'Cut Set' },
                ticks: {
                    font: { size: 10 },
                    maxRotation: 90,
                    autoSkip: true,
                    maxTicksLimit: 40,
                },
            },
            y: {
                type: 'linear' as const,
                position: 'left' as const,
                title: { display: true, text: 'Individual Contribution (%)' },
                beginAtZero: true,
                ticks: { font: { size: 11 } },
            },
            y1: {
                type: 'linear' as const,
                position: 'right' as const,
                title: { display: true, text: 'Cumulative (%)' },
                min: 0,
                max: 100,
                grid: { drawOnChartArea: false },
                ticks: {
                    font: { size: 11 },
                    callback: (value: string | number): string => `${value}%`,
                },
            },
        },
        onClick: (_event: unknown, elements: Array<{ index: number }>): void => {
            if (elements.length > 0 && onBarClick) {
                onBarClick(paretoData.allSorted[elements[0].index].index);
            }
        },
    }), [paretoData, displayLimit, onBarClick]);

    const handleExportCSV = useCallback((): void => {
        const items = paretoData.allSorted.slice(0, displayLimit);
        const header = ['Cut Set #', 'Probability', 'Contribution (%)', 'Cumulative (%)'].join(',');
        let cumSum = 0;
        const csvRows = items.map((d) => {
            const contrib = totalProbability > 0 ? (d.probability / totalProbability) * 100 : 0;
            cumSum += d.probability;
            const cumPct = totalProbability > 0 ? (cumSum / totalProbability) * 100 : 0;
            return [
                d.index,
                d.probability.toExponential(2),
                contrib.toFixed(1),
                cumPct.toFixed(1),
            ].join(',');
        });
        const csv = [header, ...csvRows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `pareto_analysis_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [paretoData, displayLimit, totalProbability]);

    if (cutSets.length === 0) return null;

    return (
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            {totalProbability === 0 ? (
                <Typography variant="body2" color="error" sx={{ py: 2, textAlign: 'center' }}>
                    All cut set probabilities are zero — Pareto chart requires non-zero probabilities.
                </Typography>
            ) : (
                <>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle2">Pareto Analysis</Typography>
                        {showSelector && (
                            <>
                                <Typography variant="caption" color="text.secondary">Show top</Typography>
                                <TextField
                                    select
                                    size="small"
                                    value={limit}
                                    onChange={(e): void => setLimit(Number(e.target.value))}
                                    sx={{ minWidth: 80, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }}
                                >
                                    {LIMIT_OPTIONS.filter((opt) => opt <= paretoData.allSorted.length).map((opt) => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                    <MenuItem value={Infinity}>All ({paretoData.allSorted.length})</MenuItem>
                                </TextField>
                                <Typography variant="caption" color="text.secondary">cut sets</Typography>
                            </>
                        )}
                        <MuiTooltip title="Export to CSV">
                            <IconButton size="small" onClick={handleExportCSV}>
                                <FileDownloadIcon fontSize="small" />
                            </IconButton>
                        </MuiTooltip>
                    </Stack>
                    <Box sx={{ height: 280 }}>
                        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                        <Chart ref={chartRef as any} type="bar" data={chartData} options={options} plugins={[thresholdLinesPlugin]} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pt: 0.5 }}>
                        {paretoData.cutSetsAt80 > 0
                            ? `${paretoData.cutSetsAt80} of ${cutSets.length} cut sets account for 80% of risk`
                            : `Top ${displayLimit} cut sets cover less than 80% — risk is widely distributed`}
                        {paretoData.cutSetsAt95 > 0 && `, ${paretoData.cutSetsAt95} for 95%`}
                        .
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default ParetoChart;
